import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';

interface Props {
  eventoId: number;
}

interface MembroEquipe {
  id: number;
  pessoa: string;
  email: string;
  funcao: string;
  status: string;
  o_que_significa: string | null;
}

const CARD = 'rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';

// Quem mais administra o evento (direção) + convite por e-mail.
export default function BlocoDirecao({ eventoId }: Props) {
  const [membros, setMembros] = useState<MembroEquipe[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [emailConvite, setEmailConvite] = useState('');
  const [convidando, setConvidando] = useState(false);
  const [removendoEmail, setRemovendoEmail] = useState<string | null>(null);
  const [confirmarRemover, setConfirmarRemover] = useState<MembroEquipe | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function carregarMembros() {
    setCarregando(true);
    const { data, error } = await supabase
      .from('vw_equipe_evento')
      .select('id, pessoa, email, funcao, status, o_que_significa')
      .eq('evento_id', eventoId)
      .order('criado_em');
    if (error) setErro(mensagemAmigavel(error));
    else setMembros((data as MembroEquipe[]) || []);
    setCarregando(false);
  }

  useEffect(() => {
    carregarMembros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  async function convidar() {
    if (!emailConvite.trim()) return;
    setConvidando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('convidar_para_evento', {
        p_evento_id: eventoId,
        p_email: emailConvite.trim(),
      });
      if (error) throw error;
      setToast(typeof data === 'string' && data ? data : 'Convite enviado.');
      setEmailConvite('');
      await carregarMembros();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setConvidando(false);
    }
  }

  async function removerMembro(email: string) {
    setRemovendoEmail(email);
    setErro(null);
    try {
      const { error } = await supabase.rpc('remover_da_equipe', {
        p_evento_id: eventoId,
        p_email: email,
      });
      if (error) throw error;
      setToast('Pessoa removida da direção.');
      setConfirmarRemover(null);
      await carregarMembros();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setRemovendoEmail(null);
    }
  }

  return (
    <div className="space-y-3">
      {erro && (
        <div className="rounded-2xl bg-danger-50 px-4 py-3 text-[14px] text-danger-700">{erro}</div>
      )}

      <div className={CARD}>
        <p className="text-[13px] text-foreground-500">
          Quem está aqui vê e mexe em tudo neste evento. Para caixa, barraca e portaria, chame por
          link em Dia do evento › Quem está servindo.
        </p>

        <div className="mt-2">
          {carregando ? (
            <div className="space-y-2">
              {[0, 1].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-2xl bg-secondary-100" />
              ))}
            </div>
          ) : membros.length === 0 ? (
            <p className="px-1 text-[14px] text-foreground-500">Ninguém além de você.</p>
          ) : (
            <ul>
              {membros.map((m) => (
                <li key={m.id} className="flex min-h-[56px] items-center gap-3 py-2">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[14px] font-bold text-primary-700">
                    {(m.pessoa || m.email).charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-medium text-foreground-900">
                      {m.pessoa || m.email} <span className="text-foreground-400">· {m.funcao}</span>
                    </p>
                    <p className="truncate text-[13px] text-foreground-500">
                      {m.status}
                      {m.o_que_significa ? ` · ${m.o_que_significa}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmarRemover(m)}
                    aria-label={`Remover ${m.pessoa || m.email}`}
                    className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl text-foreground-400 transition-colors hover:bg-danger-50 hover:text-danger-600"
                  >
                    <i className="ri-delete-bin-line text-lg" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <input
              type="email"
              value={emailConvite}
              onChange={(e) => setEmailConvite(e.target.value)}
              placeholder="e-mail da pessoa"
              className="min-w-0 flex-1 rounded-2xl border border-background-200 bg-white px-4 py-3 text-[15px] text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={convidar}
              disabled={convidando || !emailConvite.trim()}
              className="min-h-[48px] cursor-pointer whitespace-nowrap rounded-2xl border border-dashed border-primary-300 px-4 text-[14px] font-semibold text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {convidando ? 'Convidando…' : '+ Convidar operador'}
            </button>
          </div>
        </div>
      </div>

      {toast && (
        <div className="rounded-2xl bg-foreground-950 px-4 py-3 text-[14px] text-white">{toast}</div>
      )}

      {confirmarRemover && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6">
            <h3 className="text-[17px] font-bold text-foreground-950">Remover da direção?</h3>
            <p className="mt-2 text-[14px] text-foreground-500">
              {confirmarRemover.pessoa || confirmarRemover.email} perderá o acesso a este painel.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmarRemover(null)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-2xl border border-background-200 bg-white px-4 py-3 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-background-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => removerMembro(confirmarRemover.email)}
                disabled={removendoEmail === confirmarRemover.email}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-2xl bg-danger-600 px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-danger-700 disabled:opacity-50"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}